import { claudeTokens } from '@/styles/claudeTokens';

/**
 * 끼워 넣는 블록(배너)이 맞출 "그 화면 본문"의 좌우 끝선 — 바로 옆(위 자리면 아래, 아래 자리면 위) 형제 안에서
 * 처음 보이는 본문 블록 줄(카드·흰 컨테이너 — 배경색이 화면 바탕과 다르거나 테두리가 있는 상자)을 찾아 그 줄 전체의 왼쪽·오른쪽을 돌려준다.
 * 화면마다 숫자를 박지 않고 화면이 쓰는 여백을 그대로 따르기 위한 것(UI 가이드 6장 "끼워 넣는 블록").
 * - 너비가 기준 폭의 절반 미만인 상자(버튼·칩·아이콘)는 본문 블록으로 보지 않는다. 같은 줄(위 끝 8px 안)의 블록은 깊이와 상관없이 합친다(2단 상세·카드 줄).
 * - 화면에 고정된 것(하단 바·팝업)은 건너뛴다. 너무 깊이 내려가지 않게 살펴보는 상자 수를 제한한다.
 */
const PAGE_BACKGROUNDS = new Set(['rgba(0, 0, 0, 0)', 'transparent']);
const MAX_NODES = 400;

function hexToRgb(hex: string): string | null {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
    return m ? `rgb(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)})` : null;
}

function isBlock(el: Element, minWidth: number, viewportWidth: number, pageBg: Set<string>): boolean {
    const rect = el.getBoundingClientRect();
    if (rect.width < minWidth || rect.height < 20 || rect.left < -1 || rect.right > viewportWidth + 1) return false;
    const cs = getComputedStyle(el);
    if (cs.position === 'fixed' || cs.visibility === 'hidden') return false;
    return !pageBg.has(cs.backgroundColor) || parseFloat(cs.borderLeftWidth) > 0 || parseFloat(cs.borderRightWidth) > 0;
}

export function findContentEdges(slot: HTMLElement, placement: 'top' | 'bottom', baseWidth: number): { left: number; right: number } | null {
    const start = placement === 'top' ? slot.nextElementSibling : slot.previousElementSibling;
    if (!start) return null;
    const pageBg = new Set(PAGE_BACKGROUNDS);
    pageBg.add(getComputedStyle(document.body).backgroundColor);
    const appBg = hexToRgb(claudeTokens.color.bg);
    if (appBg) pageBg.add(appBg);
    const viewportWidth = window.innerWidth;
    const minWidth = baseWidth / 2;
    // 문서 순서(깊이 우선)로 처음 만나는 본문 블록
    const queue: Element[] = [start];
    let visited = 0;
    let first: Element | null = null;
    while (queue.length && visited < MAX_NODES) {
        const el = queue.shift()!;
        visited += 1;
        if (el === slot || el.closest('[data-page-banners]')) continue;
        if (isBlock(el, minWidth, viewportWidth, pageBg)) { first = el; break; }
        queue.unshift(...Array.from(el.children));
    }
    if (!first) return null;
    const top = first.getBoundingClientRect().top;
    // 같은 줄(위 끝 8px 안)에 있는 다른 블록도 합친다 — 2단 상세(왼쪽 본문 + 오른쪽 요약)처럼 깊이가 달라도
    const row: Element[] = [first];
    const nodes = Array.from(start.querySelectorAll('*')).slice(0, MAX_NODES);
    for (const el of nodes) {
        if (el === first || first.contains(el) || el.contains(first)) continue;
        if (Math.abs(el.getBoundingClientRect().top - top) >= 8) continue;
        if (isBlock(el, 40, viewportWidth, pageBg)) row.push(el);
    }
    return {
        left: Math.min(...row.map(e => e.getBoundingClientRect().left)),
        right: Math.max(...row.map(e => e.getBoundingClientRect().right)),
    };
}
